import { useState, type FormEvent } from "react";
import { apiFetch, useApi, Button, ButtonLink, Field, Input, Select, ErrorBox } from "@iga/ui";

interface Regions { regions: { id: string; label: string }[]; allowedTiers: string[] }

/** Public self-service sign-up for a new organisation (tenant). English only: no tenant, no locale yet. */
export function Signup() {
  const regions = useApi<Regions>(["public-regions"], "/api/public/regions");
  const [form, setForm] = useState({ slug: "", displayName: "", ownerEmail: "", ownerName: "", region: "", tier: "trial" });
  const [slugState, setSlugState] = useState<"unknown" | "checking" | "free" | "taken" | "reserved" | "invalid">("unknown");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function checkSlug() {
    if (!form.slug) return;
    setSlugState("checking");
    try {
      const r = await apiFetch<{ available: boolean; reason?: string }>("/api/public/tenant-requests/slug-available", { query: { slug: form.slug } });
      setSlugState(r.available ? "free" : r.reason === "reserved" ? "reserved" : r.reason === "invalid" ? "invalid" : "taken");
    } catch { setSlugState("taken"); }
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const region = form.region || regions.data?.regions[0]?.id || "default";
      const r = await apiFetch<{ message: string }>("/api/public/tenant-requests", { method: "POST", json: { ...form, region } });
      setDone(r.message);
    } catch (err) { setError(err instanceof Error ? err.message : "The request could not be sent"); } finally { setBusy(false); }
  }

  if (done) return (
    <div className="flex min-h-full items-center justify-center bg-brand-tint p-6"><div className="flex w-full max-w-md flex-col gap-3 rounded-dialog bg-surface p-7 shadow-e3">
      <h1 className="text-h2">Almost there</h1><p className="text-fg-2">{done}</p>
      <p className="text-small text-fg-3">After verification your organisation is created automatically and you receive an invitation to set your password.</p>
      <ButtonLink href="/" variant="secondary" size="touch" className="self-start">Back to sign-in</ButtonLink></div></div>);

  return (
    <div className="flex min-h-full items-center justify-center bg-brand-tint p-6">
      <form onSubmit={submit} className="flex w-full max-w-md flex-col gap-3 rounded-dialog bg-surface p-7 shadow-e3">
        <h1 className="text-h2">Request a new organisation</h1>
        <Field label="Organisation name"><Input value={form.displayName} onChange={set("displayName")} required /></Field>
        <Field label="Short name (URL)" hint={slugState === "free" ? `Available – your address becomes ${form.slug}.<domain>` : "Lower-case letters, digits and dashes, 3–40 characters"}
          error={slugState === "taken" ? "This short name is already in use" : slugState === "reserved" ? "This short name is reserved by the platform (e.g. test, admin, api)" : slugState === "invalid" ? "Only lower-case letters, digits and dashes, 3–40 characters" : undefined}>
          <Input value={form.slug} onChange={(e) => { setSlugState("unknown"); setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() })); }} onBlur={checkSlug} pattern="[a-z0-9][a-z0-9-]{1,38}[a-z0-9]" required invalid={["taken", "reserved", "invalid"].includes(slugState)} />
        </Field>
        <Field label="Your name"><Input value={form.ownerName} onChange={set("ownerName")} required /></Field>
        <Field label="Your e-mail" hint="A verification link is sent here"><Input type="email" value={form.ownerEmail} onChange={set("ownerEmail")} required /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Region"><Select value={form.region} onChange={set("region")}>{regions.data?.regions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</Select></Field>
          <Field label="Plan"><Select value={form.tier} onChange={set("tier")}>{(regions.data?.allowedTiers ?? ["trial"]).map((x) => <option key={x} value={x}>{x}</option>)}</Select></Field>
        </div>
        <ErrorBox error={error} />
        <Button type="submit" size="touch" className="w-full" loading={busy}>Send request</Button>
        <a className="text-center text-small" href="/">Back to sign-in</a>
      </form>
    </div>
  );
}
